import { ArrowBigUp, Eye, EyeOff } from "lucide-react"
import * as React from "react"

import { useFormField } from "@/components/ui/form-field"
import { IconButton } from "@/components/ui/icon-button"
import { Input, type InputProps } from "@/components/ui/input"
import { cn } from "@/lib/utils"

/*
   PasswordInput — the one password control, wherever a password is typed.

   Every screen gets the same three things: a show/hide button that is a real, keyboard-reachable
   button (not tabIndex -1), a Caps Lock warning, and the browser hints that keep a revealed
   password from being capitalised or "corrected".

   The toggle sits beside the input rather than in its `suffix`: that slot is aria-hidden, which is
   right for a unit or an icon and wrong for a button.
*/

export type PasswordInputProps = Omit<InputProps, "type">

export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput(
    {
      className,
      inputSize,
      disabled,
      onBlur,
      onKeyDown,
      onKeyUp,
      "aria-describedby": describedBy,
      ...props
    },
    ref,
  ) {
    const field = useFormField()
    const capsId = React.useId()
    const [shown, setShown] = React.useState(false)
    const [capsOn, setCapsOn] = React.useState(false)

    const readCaps = (event: React.KeyboardEvent<HTMLInputElement>) => {
      setCapsOn(event.getModifierState("CapsLock"))
    }

    // An explicit aria-describedby would replace the field's hint and error rather than join
    // them, so the three are merged here: the field's own first, then the caller's, then Caps Lock.
    const describedByIds =
      [field?.describedBy, describedBy, capsOn ? capsId : undefined].filter(Boolean).join(" ") ||
      undefined
    const isDisabled = disabled ?? field?.disabled

    return (
      <div className="flex flex-col gap-1.5">
        <div className="relative">
          <Input
            ref={ref}
            {...props}
            type={shown ? "text" : "password"}
            inputSize={inputSize}
            disabled={isDisabled}
            aria-describedby={describedByIds}
            // Managers and mobile keyboards otherwise capitalise or correct a revealed password.
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            className={cn("pr-8", className)}
            onKeyDown={(event) => {
              readCaps(event)
              onKeyDown?.(event)
            }}
            onKeyUp={(event) => {
              readCaps(event)
              onKeyUp?.(event)
            }}
            onBlur={(event) => {
              setCapsOn(false)
              onBlur?.(event)
            }}
          />
          <IconButton
            variant="ghost"
            size="sm"
            label={shown ? "Hide password" : "Show password"}
            icon={shown ? <EyeOff /> : <Eye />}
            aria-pressed={shown}
            disabled={isDisabled}
            onClick={() => {
              setShown((value) => !value)
            }}
            // No tooltip: the sign-in, invite and reset pages render outside the shell's
            // TooltipProvider, and an eye needs no caption. The label still names the button.
            noTooltip
            className={cn(
              "absolute top-1/2 -translate-y-1/2",
              inputSize === "lg" ? "right-1.5" : "right-1",
            )}
          />
        </div>

        {/* Always mounted, so a screen reader announces the warning when it appears. */}
        <p
          id={capsId}
          role="status"
          aria-live="polite"
          className="flex items-center gap-1.5 text-caption font-medium text-print empty:hidden"
        >
          {capsOn && (
            <>
              <ArrowBigUp aria-hidden="true" className="size-3.5 shrink-0" />
              Caps Lock is on
            </>
          )}
        </p>
      </div>
    )
  },
)
